import './services/tailnetTransportInstall';
import { loadAsIfUi } from '@/features/as-if/loadAsIfUi';
import { getAsIfState } from '@/features/as-if/asIfView';
import React from 'react';
import ReactDOM from 'react-dom/client';
import App, { bootRoutesFor } from './App';
import { deploymentNeedsChoosing } from './features/onboarding/deploymentChoice';
import { authStore } from './stores';
import { hasStoredAuthSession } from './services/ApiService';
import { holdForBootRequests } from './services/bootRequests';
import { isDesktopRuntime } from './desktop/desktopDetection';
import { holdEarlyPageErrors } from './desktop/earlyPageErrors';
import { preloadDesktopChrome } from './desktop/desktopChrome';
import { KeychainFallbackNotice, showRestoringSession } from './desktop/restoringSession';
import { BOOT_MARKS, markBoot } from './shell/bootMarks';
import { keepBootReadsInResourceTiming } from './shell/bootReport';
import { bootThenRender } from './shell/bootSequence';
import { loadLaunchChunks } from './shell/launchChunks';
import { allowShellEventCheck, installShellBridge } from './shell/shellBridge';
import { hydrateShellSession } from './shell/shellSessionHydration';
import { settleDeviceGetStarted } from './features/get-started/deviceGetStartedBoot';
import { PhoneTypeScaleEffect } from './styles/usePhoneTypeScaleEffect';
import { LocaleProvider } from './i18n/LocaleProvider';
import { loadMessages } from './i18n/catalogue';
import { languagePreference } from './i18n/languageInstance';
import './index.css';
import './styles/editor.css';
import './styles/markdown-toolbar.css';
import './styles/phone-type.css';

/**
 * The desktop's stored sign-in goes back before anything asks whether there is one.
 *
 * `AuthStore.initialize()` answers "signed out" from empty storage slots without making a request,
 * so restoring afterwards would be too late — the app would already have decided. On the web this
 * resolves immediately and changes nothing; `hydrateDesktopSession` returns `not-desktop` before it
 * touches anything.
 *
 * Awaited rather than raced with the app's mount because the alternative is a sign-in screen that
 * disappears on its own a moment later, which reads as a glitch even when the outcome is right.
 * RES-13a (I-1696): the window does not wait with it. The desktop paints "Restoring your session…"
 * into the root first, and the app replaces it there once the restore settles.
 */
/*
 * T-0320: the app shell's Keychain session goes back by the same rule. The bridge is installed first,
 * because the vault's read is answered by a `reply` event the shell dispatches into it.
 */
// T-0559: everything this module imports statically has been fetched, parsed and run by now.
markBoot(BOOT_MARKS.entry);
// RD-32a: before the first read, so the boot report still sees it past 250 other resources.
keepBootReadsInResourceTiming();
// T-0559: what the launch screen needs, started below once the session check is out.
const boot = bootRoutesFor(window.location.pathname);
// I-1380: and what the stores asked for while their modules ran waits for it too.
const releaseBootRequests = holdForBootRequests();
installShellBridge();
// F-0689 DG-8: the desktop webview's own errors reach the app's log from the first module on. T-0896
// (LC-6i): held here until the forwarder's chunk is in, then sent first.
const releaseEarlyPageErrors = holdEarlyPageErrors();
// T-0896 (LC-6i): the desktop app's boot-only modules, one chunk a browser tab never fetches. Each is
// in before the first render, as when the entry carried them: the forwarder, the language for the
// menu-bar menu and the quit dialog (F-0572), and the launch-smoke reporter, listening before the
// first socket can open (RES-1).
const desktopBoot = isDesktopRuntime()
  ? import('./desktop/desktopBoot').then((boot) => {
      boot.installDesktopErrorForwarding(window, undefined, undefined, releaseEarlyPageErrors());
      boot.installDesktopLanguageSync();
      void boot.startLaunchSmokeReporter();
      return boot;
    })
  : null;

/**
 * Whether the dev server double-mounts, as `StrictMode` does. A production build never does, so a
 * browser test that counts a page's requests (I-0594) asks for a single mount with this flag, or
 * it counts every read twice and cannot tell a real duplicate from the dev server's. Read only in
 * development; a production build is `StrictMode`-free whatever the flag says.
 */
function strictModeWanted(): boolean {
  try {
    return window.localStorage.getItem('featit:e2e:strict-mode') !== 'off';
  } catch {
    return true;
  }
}

const root = ReactDOM.createRoot(document.getElementById('root')!);
showRestoringSession(root);
// I-2572 (MS-2): a keychain read that fails is tried again, and the window says "Reconnecting…".
const onReconnecting = () => showRestoringSession(root, { reconnecting: true });

// I-2088: the person's language, when it is not English, is read with the sessions and before the first
// render, so no screen draws English first: the provider starts from the messages already here.
// I-1380, I-2561: the order is `bootThenRender`'s, so a unit test holds it (`shell/bootSequence.test.ts`).
void bootThenRender({
  sessions: () =>
    Promise.allSettled([
      desktopBoot?.then((boot) => boot.hydrateDesktopSession({ onReconnecting })),
      hydrateShellSession(),
      loadMessages(languagePreference.resolved()),
    ]),
  // F-0709: a tab that holds a view loads the as-if UI (its banner and words) before the first render.
  asIfUi: () => (getAsIfState().view ? loadAsIfUi().catch(() => undefined) : undefined),
  startSession: () => {
    // F-0581 OB-1: read before the first render stores a theme — see `settleDeviceGetStarted`.
    settleDeviceGetStarted({ hasSession: hasStoredAuthSession() });
    // T-0559: the session check `ProtectedRoute` would start once rendered, started now; it is the
    // one request every protected screen waits on. Same order as there: a deployment first. I-2561:
    // sent before the launch's chrome is fetched, so it never queues behind those chunks.
    if (boot.checksSession && !deploymentNeedsChoosing()) {
      void authStore.initialize();
      // And, with a session to send, the launch screen's first data beside it rather than after it.
      if (hasStoredAuthSession()) boot.startData();
    }
    // I-2561: the shell's events wait for their check, and its chunks are fetched only now, after
    // the session check (a reply, the Keychain's included, never waits: `dispatchShellEvent`).
    allowShellEventCheck();
  },
  // I-2453: what the first screen draws of the phone chrome and the app shell, before the first render.
  launchChunks: loadLaunchChunks,
  render: () => {
    markBoot(BOOT_MARKS.render);
    // A boot whose steps failed before `startSession` still lets the shell's events through.
    allowShellEventCheck();
    // I-1637: the page renews the signed-in session for the desktop's transport, and registers for it
    // only once the session is back in the page; it unregisters as the page goes away.
    if (desktopBoot) void desktopBoot.then((boot) => boot.installDesktopSessionRefresher());
    // I-1380: the launch screen's own code, fetched alongside the session check rather than after it,
    // and asked for after it: a check queued behind some forty chunks on six connections waited for
    // them. The check is sent in the call that makes it (`ApiService`'s interceptor is synchronous).
    boot.startCode();
    releaseBootRequests();
    // T-0559: the desktop app's tab bar and update button, which no other runtime draws.
    preloadDesktopChrome();
    // One tree, wrapped in StrictMode when it is wanted: the production entry holds it once, not twice.
    const app = (
      <>
        <PhoneTypeScaleEffect />
        <LocaleProvider>
          <App />
          {/* I-1971: inside the provider, so it speaks the reader's language; outside, it read English. */}
          <KeychainFallbackNotice />
        </LocaleProvider>
      </>
    );
    // The dev server alone can ask for no StrictMode; a production build folds the test away.
    root.render(
      import.meta.env.DEV && !strictModeWanted() ? app : <React.StrictMode>{app}</React.StrictMode>,
    );
  },
});
